import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchCart as fetchCartApi,
  replaceCart as replaceCartApi,
  addCartItems as addCartItemsApi,
  patchCartItem as patchCartItemApi,
  removeCartItem as removeCartItemApi,
  clearCart as clearCartApi,
} from '../api/checkout';
import { useUnifiedAuth } from './UnifiedAuthContext';
import { checkoutDebugLog, isCheckoutDebugEnabled, summarizeCartSnapshot } from '../utils/checkoutDebug';

const CheckoutCartContext = createContext(null);

const safeParse = (value) => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    console.warn('[CheckoutCartContext] Failed to clone metadata value', error);
    return null;
  }
};

export const CheckoutCartProvider = ({ children, autoLoad = true }) => {
  const { isAuthenticated, getApiToken } = useUnifiedAuth();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(autoLoad);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const debugSequenceRef = useRef(0);
  const cartSnapshotRef = useRef(null);
  const checkoutDebugEnabled = isCheckoutDebugEnabled;

  const getTokenIfAvailable = useCallback(async () => {
    if (!isAuthenticated) {
      return null;
    }
    try {
      const token = await getApiToken();
      return token || null;
    } catch (tokenError) {
      console.warn('[CheckoutCartContext] Failed to obtain API token', tokenError);
      return null;
    }
  }, [getApiToken, isAuthenticated]);

  const refreshCart = useCallback(async () => {
    const debugId = ++debugSequenceRef.current;
    const cartBefore = summarizeCartSnapshot(cartSnapshotRef.current);
    if (checkoutDebugEnabled) {
      checkoutDebugLog('info', `refreshCart:start #${debugId}`, {
        authenticated: isAuthenticated,
        cartBefore,
      });
      setDebugInfo({
        type: 'refreshCart',
        debugId,
        status: 'pending',
        timestamp: Date.now(),
        cartBefore,
        authenticated: isAuthenticated,
      });
    }
    setLoading(true);
    setError(null);
    try {
      const token = await getTokenIfAvailable();
      const data = await fetchCartApi(token);
      setCart(data);
      cartSnapshotRef.current = data;
      if (checkoutDebugEnabled) {
        checkoutDebugLog('info', `refreshCart:success #${debugId}`, {
          cartAfter: summarizeCartSnapshot(data),
        });
        setDebugInfo({
          type: 'refreshCart',
          debugId,
          status: 'success',
          timestamp: Date.now(),
          cartBefore,
          cartAfter: summarizeCartSnapshot(data),
        });
      }
    } catch (fetchError) {
      console.error('[CheckoutCartContext] Failed to load cart', fetchError);
      setError(fetchError);
      setCart(null);
      cartSnapshotRef.current = null;
      if (checkoutDebugEnabled) {
        checkoutDebugLog('warn', `refreshCart:error #${debugId}`, {
          message: fetchError?.message || 'unknown error',
          status: fetchError?.status ?? null,
        });
        setDebugInfo({
          type: 'refreshCart',
          debugId,
          status: 'error',
          timestamp: Date.now(),
          cartBefore,
          message: fetchError?.message || 'unknown error',
          statusCode: fetchError?.status ?? null,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [checkoutDebugEnabled, getTokenIfAvailable, isAuthenticated]);

  useEffect(() => {
    if (!autoLoad) {
      setLoading(false);
      return;
    }
    refreshCart();
  }, [autoLoad, refreshCart]);

  const runMutation = useCallback(async (action, mutationFn, debugContextBuilder) => {
    const debugId = ++debugSequenceRef.current;
    const cartBefore = summarizeCartSnapshot(cartSnapshotRef.current);
    const builderOutput = typeof debugContextBuilder === 'function'
      ? debugContextBuilder(cartBefore)
      : {};
    if (checkoutDebugEnabled) {
      checkoutDebugLog('info', `${action}:start #${debugId}`, {
        cartBefore,
        ...builderOutput,
      });
      setDebugInfo({
        type: action,
        debugId,
        status: 'pending',
        timestamp: Date.now(),
        cartBefore,
        payloadSummary: builderOutput?.payloadSummary ?? null,
        productSlug: builderOutput?.productSlug ?? null,
        flowContext: builderOutput?.flowContext ?? null,
      });
    }
    setPending(true);
    setError(null);
    try {
      const token = await getTokenIfAvailable();
      const updated = await mutationFn(token);
      setCart(updated);
      cartSnapshotRef.current = updated;
      if (checkoutDebugEnabled) {
        checkoutDebugLog('info', `${action}:success #${debugId}`, {
          cartBefore,
          cartAfter: summarizeCartSnapshot(updated),
        });
        setDebugInfo({
          type: action,
          debugId,
          status: 'success',
          timestamp: Date.now(),
          cartBefore,
          cartAfter: summarizeCartSnapshot(updated),
          payloadSummary: builderOutput?.payloadSummary ?? null,
          productSlug: builderOutput?.productSlug ?? null,
          flowContext: builderOutput?.flowContext ?? null,
        });
      }
      return updated;
    } catch (mutationError) {
      console.error('[CheckoutCartContext] Cart mutation failed', mutationError);
      setError(mutationError);
      if (checkoutDebugEnabled) {
        checkoutDebugLog('warn', `${action}:error #${debugId}`, {
          cartBefore,
          message: mutationError?.message || 'unknown error',
          status: mutationError?.status ?? null,
        });
        setDebugInfo({
          type: action,
          debugId,
          status: 'error',
          timestamp: Date.now(),
          cartBefore,
          message: mutationError?.message || 'unknown error',
          statusCode: mutationError?.status ?? null,
          payloadSummary: builderOutput?.payloadSummary ?? null,
          productSlug: builderOutput?.productSlug ?? null,
          flowContext: builderOutput?.flowContext ?? null,
        });
      }
      throw mutationError;
    } finally {
      setPending(false);
    }
  }, [checkoutDebugEnabled, getTokenIfAvailable]);

  const replaceCart = useCallback((items, options = {}) => {
    const payload = {
      items,
      flowContext: options.flowContext ?? cartSnapshotRef.current?.flowContext ?? null,
      metadata: options.metadata ? safeParse(options.metadata) : cartSnapshotRef.current?.metadata ?? null,
    };
    return runMutation(
      'replaceCart',
      (token) => replaceCartApi(payload, token),
      () => ({
        payloadSummary: {
          itemCount: Array.isArray(items) ? items.length : 0,
          flowContext: payload.flowContext,
          hasMetadata: Boolean(payload.metadata),
        },
      }),
    );
  }, [runMutation]);

  const addItems = useCallback((items, options = {}) => {
    const payload = {
      items,
      flowContext: options.flowContext ?? null,
      metadata: options.metadata ? safeParse(options.metadata) : undefined,
    };
    return runMutation(
      'addItems',
      (token) => addCartItemsApi(payload, token),
      () => ({
        payloadSummary: {
          itemCount: Array.isArray(items) ? items.length : 0,
          flowContext: payload.flowContext,
          hasMetadata: Boolean(payload.metadata),
        },
      }),
    );
  }, [runMutation]);

  const updateItem = useCallback((productSlug, update, options = {}) => {
    const payload = {
      ...update,
      flowContext: options.flowContext ?? null,
      cartMetadata: options.metadata ? safeParse(options.metadata) : undefined,
    };
    return runMutation(
      'updateItem',
      (token) => patchCartItemApi(productSlug, payload, token),
      () => ({
        productSlug,
        payloadSummary: {
          fields: Object.keys(update || {}),
          flowContext: payload.flowContext,
          hasMetadata: Boolean(payload.cartMetadata),
        },
      }),
    );
  }, [runMutation]);

  const removeItem = useCallback((productSlug) => runMutation(
    'removeItem',
    (token) => removeCartItemApi(productSlug, token),
    () => ({
      productSlug,
    }),
  ), [runMutation]);

  const clearCart = useCallback(() => runMutation(
    'clearCart',
    (token) => clearCartApi(token),
    () => ({}),
  ), [runMutation]);

  const setFlowContext = useCallback((flowContext) => runMutation(
    'setFlowContext',
    (token) => addCartItemsApi({ items: [], flowContext }, token),
    () => ({
      flowContext,
    }),
  ), [runMutation]);

  useEffect(() => {
    cartSnapshotRef.current = cart;
  }, [cart]);

  const value = useMemo(() => ({
    cart,
    loading,
    pending,
    error,
    refreshCart,
    replaceCart,
    addItems,
    updateItem,
    removeItem,
    clearCart,
    setFlowContext,
    debugInfo: checkoutDebugEnabled ? debugInfo : null,
    debugEnabled: checkoutDebugEnabled,
  }), [cart, loading, pending, error, refreshCart, replaceCart, addItems, updateItem, removeItem, clearCart, setFlowContext,
    checkoutDebugEnabled, debugInfo]);

  return (
    <CheckoutCartContext.Provider value={value}>
      {children}
    </CheckoutCartContext.Provider>
  );
};

export const useCheckoutCart = () => {
  const context = useContext(CheckoutCartContext);
  if (!context) {
    throw new Error('useCheckoutCart must be used within a CheckoutCartProvider');
  }
  return context;
};
